import React from 'react';

/**
 * ServiceBackdrop — animated decorative SVG layer placed behind hero / section content.
 * Renders floating engineering-discipline-specific glyphs:
 *   • Surveying: compass-rose, scale-bar, monument pin, contour rings, transit
 *   • BIM / Arch: 3D cube wireframe, layer stack, north arrow
 *   • Structural: I-beam, bolt, dimension arrow
 *   • MEP: pipe elbow, valve, conduit
 *   • Civil: road profile, curb section
 *   • Plant: vessel, pump symbol, P&ID circle
 *   • GIS: contour lines, lat-long crosshair
 *   • Default: gear, T-square, ruler
 *
 * All glyphs animate independently via CSS keyframes (drift, spin, pulse).
 * Glyphs are decorative and absolutely positioned — they do not interact.
 */

const STYLE = `
@keyframes sb-drift  { 0%,100% { transform: translate(0,0) }   50% { transform: translate(8px,-12px) } }
@keyframes sb-drift2 { 0%,100% { transform: translate(0,0) }   50% { transform: translate(-10px,10px) } }
@keyframes sb-spin   { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
@keyframes sb-spin-r { from { transform: rotate(0deg) } to { transform: rotate(-360deg) } }
@keyframes sb-pulse  { 0%,100% { opacity: 0.18 } 50% { opacity: 0.42 } }
@keyframes sb-bob    { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
.sb-el { position: absolute; pointer-events: none; }
.sb-el.theme-dark  { opacity: 0.65; filter: drop-shadow(0 2px 6px rgba(148, 163, 184, 0.35)); }
.sb-el.theme-light { opacity: 0.22; }
.sb-drift  { animation: sb-drift  9s ease-in-out infinite; }
.sb-drift2 { animation: sb-drift2 11s ease-in-out infinite; }
.sb-spin   { animation: sb-spin 40s linear infinite; transform-origin: center; }
.sb-spin-r { animation: sb-spin-r 60s linear infinite; transform-origin: center; }
.sb-pulse  { animation: sb-pulse 4s ease-in-out infinite; }
.sb-bob    { animation: sb-bob 5s ease-in-out infinite; }
`;

// — Reusable glyph SVGs —
const NorthArrow = ({ color }) => (
  <svg viewBox="0 0 80 80" width="80" height="80">
    <circle cx="40" cy="40" r="34" stroke={color} strokeOpacity="0.6" strokeWidth="1" fill="none" />
    <circle cx="40" cy="40" r="22" stroke={color} strokeOpacity="0.4" strokeWidth="0.8" fill="none" strokeDasharray="3,3" />
    <polygon points="40,8 47,42 40,38 33,42" fill={color} fillOpacity="0.6" stroke={color} />
    <text x="40" y="74" textAnchor="middle" fontSize="10" fill={color} fontWeight="700">N</text>
  </svg>
);

const ScaleBar = ({ color, alt }) => (
  <svg viewBox="0 0 200 30" width="200" height="30">
    <rect x="0" y="6" width="40" height="10" fill={color} fillOpacity="0.5" />
    <rect x="40" y="6" width="40" height="10" fill={alt} fillOpacity="0.5" />
    <rect x="80" y="6" width="40" height="10" fill={color} fillOpacity="0.5" />
    <rect x="120" y="6" width="40" height="10" fill={alt} fillOpacity="0.5" />
    <rect x="0" y="6" width="160" height="10" stroke={color} fill="none" strokeWidth="0.8" />
    <text x="0"  y="26" fontSize="7" fill={color}>0</text>
    <text x="78" y="26" fontSize="7" fill={color}>50</text>
    <text x="156" y="26" fontSize="7" fill={color}>100 ft</text>
  </svg>
);

const ContourRings = ({ color, alt }) => (
  <svg viewBox="0 0 220 160" width="220" height="160">
    {[60, 48, 36, 26, 16].map((r, i) => (
      <ellipse key={r} cx="110" cy="80" rx={r * 1.4} ry={r * 0.9} stroke={i % 2 ? alt : color} strokeOpacity={0.35 + i * 0.07} strokeWidth="0.9" fill="none" />
    ))}
  </svg>
);

const MonumentPin = ({ color, alt }) => (
  <svg viewBox="0 0 60 80" width="40" height="55">
    <circle cx="30" cy="22" r="14" stroke={alt} strokeWidth="1.4" fill="none" />
    <line x1="30" y1="6" x2="30" y2="38" stroke={alt} strokeWidth="1" />
    <line x1="14" y1="22" x2="46" y2="22" stroke={alt} strokeWidth="1" />
    <line x1="30" y1="36" x2="30" y2="74" stroke={color} strokeWidth="1.2" />
    <polygon points="22,68 38,68 30,80" fill={color} fillOpacity="0.7" />
  </svg>
);

const Transit = ({ color }) => (
  <svg viewBox="0 0 90 100" width="60" height="70">
    <line x1="45" y1="100" x2="20" y2="80" stroke={color} strokeWidth="1.2" />
    <line x1="45" y1="100" x2="70" y2="80" stroke={color} strokeWidth="1.2" />
    <line x1="45" y1="100" x2="45" y2="60" stroke={color} strokeWidth="1.2" />
    <rect x="32" y="40" width="26" height="22" stroke={color} strokeWidth="1.2" fill="none" />
    <line x1="20" y1="50" x2="32" y2="50" stroke={color} strokeWidth="1.2" />
    <line x1="58" y1="50" x2="76" y2="50" stroke={color} strokeWidth="1.2" />
    <circle cx="20" cy="50" r="3" fill={color} />
  </svg>
);

const CubeWire = ({ color, alt }) => (
  <svg viewBox="0 0 120 120" width="120" height="120">
    <polygon points="30,40 70,20 110,40 110,90 70,110 30,90" stroke={color} strokeWidth="1.2" fill="none" />
    <line x1="30" y1="40" x2="70" y2="60" stroke={alt} strokeWidth="1" />
    <line x1="110" y1="40" x2="70" y2="60" stroke={alt} strokeWidth="1" />
    <line x1="70" y1="60" x2="70" y2="110" stroke={alt} strokeWidth="1" />
  </svg>
);

const LayerStack = ({ color, alt }) => (
  <svg viewBox="0 0 120 80" width="120" height="80">
    {[0, 14, 28].map((y, i) => (
      <polygon key={y} points={`20,${30 + y} 60,${15 + y} 100,${30 + y} 60,${45 + y}`} stroke={i === 1 ? alt : color} strokeWidth="1.1" fill="none" />
    ))}
  </svg>
);

const IBeam = ({ color, alt }) => (
  <svg viewBox="0 0 100 80" width="100" height="80">
    <rect x="10" y="14" width="80" height="6" fill={alt} fillOpacity="0.5" />
    <rect x="44" y="20" width="12" height="40" fill={color} fillOpacity="0.4" />
    <rect x="10" y="60" width="80" height="6" fill={alt} fillOpacity="0.5" />
    {[20, 40, 60, 80].map(x => <circle key={x} cx={x} cy="17" r="1.5" fill={color} />)}
  </svg>
);

const PipeElbow = ({ color, alt }) => (
  <svg viewBox="0 0 110 110" width="90" height="90">
    <path d="M 10 30 L 60 30 Q 80 30 80 50 L 80 100" stroke={color} strokeWidth="4" fill="none" strokeLinecap="round" />
    <circle cx="60" cy="30" r="4" fill={alt} />
    <circle cx="80" cy="50" r="4" fill={alt} />
    <line x1="76" y1="46" x2="84" y2="54" stroke={alt} strokeWidth="1" />
  </svg>
);

const Valve = ({ color }) => (
  <svg viewBox="0 0 80 80" width="55" height="55">
    <polygon points="10,20 10,60 40,40" stroke={color} strokeWidth="1.4" fill="none" />
    <polygon points="70,20 70,60 40,40" stroke={color} strokeWidth="1.4" fill="none" />
    <line x1="40" y1="40" x2="40" y2="10" stroke={color} strokeWidth="1" />
    <line x1="30" y1="10" x2="50" y2="10" stroke={color} strokeWidth="1.4" />
  </svg>
);

const RoadProfile = ({ color, alt }) => (
  <svg viewBox="0 0 200 80" width="180" height="60">
    <path d="M 0 50 Q 50 20 100 50 T 200 35" stroke={color} strokeWidth="1.4" fill="none" />
    <path d="M 0 65 Q 50 35 100 65 T 200 50" stroke={color} strokeWidth="1.4" fill="none" />
    <path d="M 0 58 Q 50 28 100 58 T 200 43" stroke={alt} strokeWidth="0.8" strokeDasharray="8,5" fill="none" />
  </svg>
);

const PIDVessel = ({ color, alt }) => (
  <svg viewBox="0 0 70 110" width="55" height="90">
    <rect x="15" y="20" width="40" height="70" stroke={alt} strokeWidth="1.3" fill="none" />
    <ellipse cx="35" cy="20" rx="20" ry="7" stroke={alt} strokeWidth="1.3" fill="none" />
    <ellipse cx="35" cy="90" rx="20" ry="7" stroke={alt} strokeWidth="1.3" fill="none" />
    <text x="35" y="58" textAnchor="middle" fontSize="8" fill={color} fontWeight="700">V-101</text>
  </svg>
);

const Crosshair = ({ color, alt }) => (
  <svg viewBox="0 0 100 100" width="80" height="80">
    <circle cx="50" cy="50" r="36" stroke={color} strokeOpacity="0.6" fill="none" strokeDasharray="2,3" />
    <circle cx="50" cy="50" r="22" stroke={alt} strokeOpacity="0.6" fill="none" />
    <line x1="6" y1="50" x2="94" y2="50" stroke={color} strokeOpacity="0.5" />
    <line x1="50" y1="6" x2="50" y2="94" stroke={color} strokeOpacity="0.5" />
    <circle cx="50" cy="50" r="3" fill={alt} />
  </svg>
);

const Gear = ({ color }) => (
  <svg viewBox="0 0 80 80" width="70" height="70">
    <g>
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i * 30) * Math.PI / 180;
        return <rect key={`serv-1-${i}`} x="36" y="2" width="8" height="12" fill={color} fillOpacity="0.5" transform={`rotate(${i * 30} 40 40)`} />;
      })}
      <circle cx="40" cy="40" r="22" stroke={color} strokeWidth="2" fill="none" />
      <circle cx="40" cy="40" r="8" stroke={color} strokeWidth="1.4" fill="none" />
    </g>
  </svg>
);

const TSquare = ({ color, alt }) => (
  <svg viewBox="0 0 140 100" width="130" height="90">
    <rect x="6" y="10" width="20" height="80" stroke={color} strokeWidth="1.3" fill="none" />
    <rect x="6" y="44" width="128" height="12" stroke={alt} strokeWidth="1.3" fill="none" />
    {Array.from({ length: 12 }).map((_, i) => (
      <line key={`serv-2-${i}`} x1={26 + i * 9} y1="44" x2={26 + i * 9} y2={i % 2 ? "50" : "56"} stroke={alt} strokeWidth="0.8" />
    ))}
  </svg>
);

const TreeIcon = ({ color, alt }) => (
  <svg viewBox="0 0 60 80" width="48" height="64">
    <line x1="30" y1="48" x2="30" y2="76" stroke={color} strokeWidth="2" />
    <circle cx="30" cy="32" r="18" stroke={alt} strokeWidth="1.5" fill="none" />
    <circle cx="30" cy="32" r="10" stroke={color} strokeWidth="1" strokeOpacity="0.6" fill="none" />
  </svg>
);

const TowerIcon = ({ color, alt }) => (
  <svg viewBox="0 0 80 110" width="50" height="80">
    <polygon points="40,8 30,100 50,100" stroke={color} strokeWidth="1.4" fill="none" />
    <line x1="33" y1="80" x2="47" y2="80" stroke={alt} strokeWidth="0.9" />
    <line x1="32" y1="65" x2="48" y2="65" stroke={alt} strokeWidth="0.9" />
    <line x1="31" y1="50" x2="49" y2="50" stroke={alt} strokeWidth="0.9" />
    <line x1="30" y1="35" x2="50" y2="35" stroke={alt} strokeWidth="0.9" />
    <circle cx="40" cy="6" r="3" fill={alt} />
    <path d="M 28 14 Q 40 22 52 14" stroke={alt} strokeWidth="0.8" fill="none" />
  </svg>
);

// — Variant → which glyphs to render and where —
const VARIANTS = {
  survey: [
    { Cmp: NorthArrow,   x: '4%',  y: '8%',  size: 1.0, anim: 'sb-spin' },
    { Cmp: ScaleBar,     x: '6%',  y: '78%', size: 1.0, anim: 'sb-drift' },
    { Cmp: MonumentPin,  x: '88%', y: '70%', size: 1.0, anim: 'sb-bob' },
    { Cmp: ContourRings, x: '70%', y: '14%', size: 1.0, anim: 'sb-drift2' },
    { Cmp: Transit,      x: '82%', y: '40%', size: 1.0, anim: 'sb-bob' },
    { Cmp: Crosshair,    x: '40%', y: '60%', size: 0.85, anim: 'sb-pulse' }
  ],
  gis: [
    { Cmp: ContourRings, x: '5%',  y: '10%', size: 1.0, anim: 'sb-drift' },
    { Cmp: ContourRings, x: '65%', y: '60%', size: 1.1, anim: 'sb-drift2' },
    { Cmp: NorthArrow,   x: '88%', y: '12%', size: 1.0, anim: 'sb-spin' },
    { Cmp: Crosshair,    x: '40%', y: '70%', size: 1.0, anim: 'sb-pulse' },
    { Cmp: ScaleBar,     x: '20%', y: '85%', size: 1.0, anim: 'sb-drift' }
  ],
  bim: [
    { Cmp: CubeWire,    x: '6%',  y: '15%', size: 1.0, anim: 'sb-drift' },
    { Cmp: CubeWire,    x: '78%', y: '70%', size: 0.85, anim: 'sb-drift2' },
    { Cmp: LayerStack,  x: '70%', y: '12%', size: 1.0, anim: 'sb-bob' },
    { Cmp: TSquare,     x: '10%', y: '75%', size: 0.8, anim: 'sb-drift' },
    { Cmp: NorthArrow,  x: '88%', y: '40%', size: 0.7, anim: 'sb-spin' }
  ],
  structural: [
    { Cmp: IBeam,      x: '5%',  y: '15%', size: 1.0, anim: 'sb-drift' },
    { Cmp: IBeam,      x: '70%', y: '70%', size: 1.0, anim: 'sb-drift2' },
    { Cmp: Gear,       x: '85%', y: '15%', size: 1.0, anim: 'sb-spin' },
    { Cmp: TSquare,    x: '60%', y: '20%', size: 0.7, anim: 'sb-bob' }
  ],
  mep: [
    { Cmp: PipeElbow, x: '6%',  y: '14%', size: 1.0, anim: 'sb-drift' },
    { Cmp: Valve,     x: '82%', y: '20%', size: 1.0, anim: 'sb-spin' },
    { Cmp: PipeElbow, x: '78%', y: '70%', size: 0.85, anim: 'sb-drift2' },
    { Cmp: Valve,     x: '40%', y: '78%', size: 0.8, anim: 'sb-pulse' }
  ],
  civil: [
    { Cmp: RoadProfile, x: '4%',  y: '20%', size: 1.0, anim: 'sb-drift' },
    { Cmp: RoadProfile, x: '40%', y: '78%', size: 0.95, anim: 'sb-drift2' },
    { Cmp: NorthArrow,  x: '88%', y: '12%', size: 1.0, anim: 'sb-spin' },
    { Cmp: ScaleBar,    x: '60%', y: '15%', size: 0.9, anim: 'sb-bob' }
  ],
  plant: [
    { Cmp: PIDVessel, x: '6%',  y: '14%', size: 1.0, anim: 'sb-bob' },
    { Cmp: Valve,     x: '40%', y: '20%', size: 1.0, anim: 'sb-spin' },
    { Cmp: PipeElbow, x: '70%', y: '60%', size: 1.0, anim: 'sb-drift' },
    { Cmp: PIDVessel, x: '85%', y: '70%', size: 0.85, anim: 'sb-drift2' }
  ],
  manufacturing: [
    { Cmp: Gear,    x: '5%',  y: '12%', size: 1.0, anim: 'sb-spin' },
    { Cmp: Gear,    x: '85%', y: '70%', size: 0.85, anim: 'sb-spin-r' },
    { Cmp: TSquare, x: '60%', y: '78%', size: 0.85, anim: 'sb-drift' },
    { Cmp: IBeam,   x: '70%', y: '15%', size: 0.8, anim: 'sb-bob' }
  ],
  graphics: [
    { Cmp: CubeWire,  x: '6%',  y: '14%', size: 0.9, anim: 'sb-spin' },
    { Cmp: Crosshair, x: '80%', y: '20%', size: 1.0, anim: 'sb-pulse' },
    { Cmp: LayerStack, x: '70%', y: '70%', size: 1.0, anim: 'sb-drift2' }
  ],
  signage: [
    { Cmp: TowerIcon, x: '8%', y: '14%', size: 1.0, anim: 'sb-bob' },
    { Cmp: TSquare,   x: '70%', y: '74%', size: 0.95, anim: 'sb-drift' },
    { Cmp: Gear,      x: '85%', y: '18%', size: 0.8, anim: 'sb-spin' }
  ],
  millwork: [
    { Cmp: TSquare,  x: '5%',  y: '14%', size: 1.0, anim: 'sb-drift' },
    { Cmp: LayerStack, x: '78%', y: '20%', size: 0.9, anim: 'sb-drift2' },
    { Cmp: CubeWire, x: '70%', y: '70%', size: 0.85, anim: 'sb-bob' }
  ],
  geotech: [
    { Cmp: ContourRings, x: '5%', y: '14%', size: 1.0, anim: 'sb-drift' },
    { Cmp: MonumentPin,  x: '85%', y: '70%', size: 1.0, anim: 'sb-bob' },
    { Cmp: TreeIcon,     x: '40%', y: '76%', size: 0.85, anim: 'sb-drift2' }
  ],
  // Specific survey-sub-page variants reuse 'survey' base + theme topper
  tower:   [ { Cmp: TowerIcon, x: '8%', y: '14%', size: 1.0, anim: 'sb-bob' }, { Cmp: NorthArrow, x: '85%', y: '18%', size: 0.9, anim: 'sb-spin' }, { Cmp: ScaleBar, x: '20%', y: '80%', size: 1.0, anim: 'sb-drift' } ],
  tree:    [ { Cmp: TreeIcon,  x: '6%', y: '14%', size: 1.0, anim: 'sb-bob' }, { Cmp: TreeIcon,   x: '80%', y: '20%', size: 0.85, anim: 'sb-drift' }, { Cmp: TreeIcon, x: '70%', y: '74%', size: 0.9, anim: 'sb-drift2' }, { Cmp: NorthArrow, x: '40%', y: '78%', size: 0.8, anim: 'sb-spin' } ],
  pipeline:[ { Cmp: PipeElbow, x: '5%', y: '14%', size: 1.0, anim: 'sb-drift' }, { Cmp: Valve, x: '80%', y: '20%', size: 1.0, anim: 'sb-spin' }, { Cmp: RoadProfile, x: '20%', y: '76%', size: 1.0, anim: 'sb-drift2' } ],
  default: [
    { Cmp: Gear,    x: '6%',  y: '14%', size: 1.0, anim: 'sb-spin' },
    { Cmp: TSquare, x: '70%', y: '76%', size: 0.85, anim: 'sb-drift' },
    { Cmp: NorthArrow, x: '86%', y: '18%', size: 0.85, anim: 'sb-spin-r' }
  ]
};

// Map service slug → variant
const SLUG_TO_VARIANT = {
  'land-surveying': 'survey',
  'land-development': 'survey',
  'gis-geospatial': 'gis',
  'environmental-engineering': 'gis',
  'mep-engineering': 'mep',
  'facility-management': 'mep',
  'bim-services': 'bim',
  '3d-modeling': 'bim',
  'architectural': 'bim',
  'structural-engineering': 'structural',
  'steel-detailing': 'structural',
  'plant-engineering': 'plant',
  'process-equipment': 'plant',
  'oil-gas-energy': 'plant',
  'civil-engineering': 'civil',
  'transportation': 'civil',
  'water-wastewater': 'civil',
  'mechanical-engineering': 'manufacturing',
  'manufacturing': 'manufacturing',
  'graphics-services': 'graphics',
  'rendering-animation': 'graphics',
  'premedia-services': 'graphics',
  'publishing-services': 'graphics',
  'signage-engineering': 'signage',
  'lumber-millwork': 'millwork',
  'construction-engineering': 'millwork',
  'engineering-designs': 'millwork',
  'geotechnical-engineering': 'geotech',
  // survey sub-types
  'alta-surveys': 'survey',
  'boundary-surveys': 'survey',
  'topographic-surveys': 'survey',
  'mortgage-location-surveys': 'survey',
  'as-built-surveys': 'survey',
  'right-of-way-surveys': 'survey',
  'tower-surveys': 'tower',
  'pipeline-surveys': 'pipeline',
  'subdivision-surveys': 'survey',
  'tree-location-surveys': 'tree'
};

const ServiceBackdrop = ({ variant, slug, accent, accentAlt, theme = 'dark', className = '' }) => {
  const v = variant || SLUG_TO_VARIANT[slug] || 'default';
  const items = VARIANTS[v] || VARIANTS.default;
  const isLight = theme === 'light';
  const a  = accent    || (isLight ? '#3f4754' : '#94a3b8');
  const aa = accentAlt || (isLight ? '#9a3412' : '#f97316');
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} data-testid="service-backdrop" data-variant={v} data-theme={theme}>
      <style>{STYLE}</style>
      {items.map((it, i) => (
        <div
          key={`${v}-${i}`}
          className={`sb-el theme-${theme} ${it.anim}`}
          style={{
            left: it.x,
            top: it.y,
            transform: `scale(${it.size})`,
            animationDelay: `${i * 0.6}s`
          }}
        >
          <it.Cmp color={a} alt={aa} />
        </div>
      ))}
    </div>
  );
};

export default ServiceBackdrop;
